/**
 * SecurityLockScreen — A_25550 / A_25551
 *
 * Full-screen blocker rendered via portal to document.body so it lives
 * OUTSIDE #root. While mounted, #root is marked `inert` (modern browsers)
 * or `aria-hidden + tabindex=-1` (fallback) so the underlying tree is
 * unfocusable / ignored by assistive tech but the lock screen is fully
 * readable. The "Update now" CTA reuses applyUpdate() so the update path
 * remains available even while locked (A_25551).
 */

import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import './SecurityLockScreen.css';

const ROOT_ID = 'root';

const formatIso = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

/**
 * Allow-list the supportContact scheme to defang `javascript:` and other
 * dangerous URIs from a tampered manifest. Returns a safe href or null
 * (in which case the contact renders as plain text).
 */
const sanitiseSupportHref = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const v = value.trim();
  // Bare email → mailto:
  if (/^[^\s:@]+@[^\s:@]+\.[^\s:@]+$/.test(v)) {
    return `mailto:${v}`;
  }
  // Explicit scheme — only mailto: and https: are allowed.
  const match = /^([a-z][a-z0-9+.-]*):/i.exec(v);
  if (!match) return null;
  const scheme = match[1].toLowerCase();
  if (scheme === 'mailto' || scheme === 'https') return v;
  return null;
};

const SecurityLockScreen = ({
  currentVersion,
  minVersion,
  lockAt,
  commitId,
  buildTime,
  supportContact,
  securityNotice,
  onUpdate,
}) => {
  const { t } = useI18n();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const titleId = useId();
  const descId = useId();

  // Inert/aria-hide the underlying tree while the lock is mounted.
  useEffect(() => {
    const rootEl = document.getElementById(ROOT_ID);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    let appliedInert = false;
    let appliedAriaHidden = false;
    let appliedTabIndex = false;

    if (rootEl) {
      if ('inert' in HTMLElement.prototype) {
        rootEl.inert = true;
        appliedInert = true;
      } else {
        rootEl.setAttribute('aria-hidden', 'true');
        rootEl.setAttribute('tabindex', '-1');
        appliedAriaHidden = true;
        appliedTabIndex = true;
      }
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      if (rootEl) {
        if (appliedInert) rootEl.inert = false;
        if (appliedAriaHidden) rootEl.removeAttribute('aria-hidden');
        if (appliedTabIndex) rootEl.removeAttribute('tabindex');
      }
    };
  }, []);

  let badge = null;
  if (securityNotice === 'critical') {
    badge = (
      <span
        className="security-lock__badge security-lock__badge--critical"
        data-testid="security-lock-badge-critical"
      >
        {t('SecurityUpdate.modal.criticalBadge', {}, 'Critical')}
      </span>
    );
  } else if (securityNotice === 'recommended') {
    badge = (
      <span
        className="security-lock__badge security-lock__badge--recommended"
        data-testid="security-lock-badge-recommended"
      >
        {t('SecurityUpdate.modal.recommendedBadge', {}, 'Recommended')}
      </span>
    );
  }

  const supportHref = sanitiseSupportHref(supportContact);

  const content = (
    <div
      className="security-lock"
      data-testid="security-lock-screen"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div className="security-lock__card">
        <div className="security-lock__icon" aria-hidden="true">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <div className="security-lock__title-row">
          <h1 id={titleId} className="security-lock__title">
            {t('SecurityUpdate.locked.title', {}, 'Update required to continue')}
          </h1>
          {badge}
        </div>
        <p id={descId} className="security-lock__body">
          {t(
            'SecurityUpdate.locked.body',
            {},
            'Your installed version is below the required security minimum and access is blocked until you update.',
          )}
        </p>
        <button
          type="button"
          className="security-lock__primary"
          data-testid="security-lock-update-now"
          onClick={onUpdate}
        >
          {t('SecurityUpdate.locked.updateNow', {}, 'Update now')}
        </button>

        <button
          type="button"
          className="security-lock__details-toggle"
          aria-expanded={detailsOpen}
          aria-controls={`${titleId}-details`}
          data-testid="security-lock-details-toggle"
          onClick={() => setDetailsOpen((v) => !v)}
        >
          {t('SecurityUpdate.locked.details', {}, 'Technical details')}
          <span aria-hidden="true">{detailsOpen ? '▲' : '▼'}</span>
        </button>

        {detailsOpen && (
          <dl
            id={`${titleId}-details`}
            className="security-lock__details"
            data-testid="security-lock-details"
          >
            <dt>{t('SecurityUpdate.locked.currentVersionLabel', {}, 'Installed version')}</dt>
            <dd>{currentVersion || '—'}</dd>
            <dt>{t('SecurityUpdate.locked.requiredVersionLabel', {}, 'Required minimum')}</dt>
            <dd>{minVersion || '—'}</dd>
            <dt>{t('SecurityUpdate.locked.lockedSinceLabel', {}, 'Locked since')}</dt>
            <dd>{formatIso(lockAt)}</dd>
            <dt>{t('SecurityUpdate.locked.buildLabel', {}, 'Build')}</dt>
            <dd>{commitId || '—'}</dd>
            <dt>{t('SecurityUpdate.locked.buildTimeLabel', {}, 'Built')}</dt>
            <dd>{formatIso(buildTime)}</dd>
            <dt>{t('SecurityUpdate.locked.supportLabel', {}, 'Support')}</dt>
            <dd>
              {supportHref
                ? (
                  <a
                    href={supportHref}
                    // target="_blank" is meaningful only for https; mailto:
                    // is typically handled by an external app handler.
                    target={supportHref.startsWith('https:') ? '_blank' : undefined}
                    rel="noopener noreferrer"
                  >
                    {supportContact}
                  </a>
                )
                : (supportContact || '—')}
            </dd>
          </dl>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

SecurityLockScreen.propTypes = {
  currentVersion: PropTypes.string,
  minVersion: PropTypes.string,
  lockAt: PropTypes.string,
  commitId: PropTypes.string,
  buildTime: PropTypes.string,
  supportContact: PropTypes.string,
  securityNotice: PropTypes.oneOf(['critical', 'recommended']),
  onUpdate: PropTypes.func.isRequired,
};

export default SecurityLockScreen;
